import { useEffect, useRef } from 'react';
import { AuthShell } from '@/components/auth/AuthShell';
import { useAuth } from '@/hooks/useAuth';

/**
 * Google OAuth callback handler · /auth/callback · slot SM.3.
 *
 * supabase-js (detectSessionInUrl + PKCE) processes the OAuth redirect at
 * client init — by the time this screen renders, the session is being or has
 * been established. This screen just waits for the auth state to settle, then
 * routes: authed → onSuccess, not authed → onFailure.
 *
 * `hash` is kept in the props for router compatibility but is no longer parsed
 * by hand (supabase-js reads the URL itself).
 */

export interface OAuthCallbackScreenProps {
  hash: string;
  onSuccess: () => void;
  onFailure: () => void;
}

export function OAuthCallbackScreen({ onSuccess, onFailure }: OAuthCallbackScreenProps) {
  const auth = useAuth();
  const done = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onFailureRef = useRef(onFailure);
  onFailureRef.current = onFailure;

  useEffect(() => {
    if (done.current) return;
    // Wait for supabase-js to finish processing the redirect URL.
    if (auth.isLoading) return;
    done.current = true;
    if (auth.isAuthed) {
      onSuccessRef.current();
    } else {
      onFailureRef.current();
    }
  }, [auth.isLoading, auth.isAuthed]);

  return (
    <AuthShell title="Подключаемся…" subtitle="Получаем профиль из Google.">
      <div className="auth-pending">
        <div className="auth-pending__icon" aria-hidden="true">
          🔄
        </div>
      </div>
    </AuthShell>
  );
}
